import {
  Button,
  Col,
  Form,
  Input,
  message,
  Modal,
  Row,
  Spin,
  Typography,
} from "antd";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import cnt from "../../../../services/definedConstant";
import { BOM } from "../../../../services/definedMethod";
import { apiConfig } from "../../customer.config";

const { Text } = Typography;

// OTP Input Component for 5-character OTP
const OTPInput = ({ value, onChange, length = 5, clearInputs }) => {
  const inputRefs = useRef([]);

  useEffect(() => {
    if (clearInputs) {
      inputRefs.current.forEach((input) => {
        if (input) {
          input.input.value = "";
        }
      });
      inputRefs.current[0]?.focus();
    }
  }, [clearInputs]);

  const handleChange = (index, inputValue) => {
    const newValue = value || "";
    const valueArray = newValue.split("");
    valueArray[index] = inputValue;

    const newOTP = valueArray.join("").slice(0, length);
    onChange(newOTP);

    if (inputValue && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !value?.[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, length);
    onChange(pastedData);

    const nextIndex = Math.min(pastedData.length, length - 1);
    inputRefs.current[nextIndex]?.focus();
  };

  return (
    <Row gutter={8} justify="center">
      {Array.from({ length }, (_, index) => (
        <Col key={index}>
          <Input
            ref={(el) => (inputRefs.current[index] = el)}
            maxLength={1}
            value={value?.[index] || ""}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            style={{
              width: 40,
              height: 40,
              textAlign: "center",
              fontSize: "16px",
              fontWeight: "bold",
            }}
            placeholder="0"
          />
        </Col>
      ))}
    </Row>
  );
};

// Main OTP Modal Component
const OTPBalanceModal = forwardRef((props, ref) => {
  const { userData, onBalanceRetrieved } = props;
  const { session } = userData;
  const [form] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingOTP, setIsSendingOTP] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [currentAccountId, setCurrentAccountId] = useState(null);
  const [currentAccountData, setCurrentAccountData] = useState(null);
  const [clearInputs, setClearInputs] = useState(false);
  const [balanceData, setBalanceData] = useState(null);
  const [showBalance, setShowBalance] = useState(false);

  useImperativeHandle(ref, () => ({
    showModal(accountId, accountData = null) {
      setCurrentAccountId(accountId);
      setCurrentAccountData(accountData);
      setIsModalOpen(true);
      setShowBalance(false);
      setBalanceData(null);
      sendOTP(accountId);
    },
  }));

  const clearOTPInputs = () => {
    setOtpValue("");
    form.setFieldsValue({ token: "" });
    setClearInputs(true);
    setTimeout(() => setClearInputs(false), 100);
  };

  const sendOTP = (accountId) => {
    if (!accountId) {
      BOM.AlertMsg(cnt.DANGER, "Account ID is required");
      setIsModalOpen(false);
      return;
    }

    const url = `${apiConfig.savingsAccounts}/${accountId}`;
    const params = {
      command: "requestViewBalance",
    };

    const body = {
      method: "POST",
      session,
      params,
    };

    console.log("Sending OTP for Account ID:", accountId);
    setIsSendingOTP(true);
    BOM.FetchReqAction(body, url, (err, res) => {
      setIsSendingOTP(false);

      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.errors?.[0]?.defaultUserMessage || "Failed to send OTP"
        );
        setIsModalOpen(false);
      } else {
        setOtpSent(true);
        BOM.AlertMsg(
          cnt.SUCCESS,
          "OTP sent successfully to your registered email"
        );
      }
    });
  };

  const verifyOTPAndGetBalance = () => {
    if (!otpValue || otpValue.length !== 5) {
      message.error("Please enter a valid 5-character OTP");
      return;
    }

    if (!currentAccountId) {
      message.error("Account ID is missing");
      return;
    }

    setIsLoading(true);
    const url = `${apiConfig.savingsAccounts}/${currentAccountId}`;
    const params = {
      command: "verifyViewBalance",
    };

    const body = {
      session,
      token: otpValue,
      method: "POST",
      params,
    };

    BOM.FetchReqAction(body, url, (err, res) => {
      setIsLoading(false);

      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.errors?.[0]?.defaultUserMessage || "OTP verification failed"
        );
        clearOTPInputs();
      } else {
        BOM.AlertMsg(cnt.SUCCESS, "Balance retrieved successfully");

        // Extract balance information from response
        const balance = {
          availableBalance:
            currentAccountData?.accountBalance ||
            currentAccountData?.availableBalance ||
            0,
          accountBalance: currentAccountData?.accountBalance || 0,
          currency:
            currentAccountData?.currency?.displaySymbol ||
            currentAccountData?.currency?.code ||
            "NGN",
        };
        setBalanceData(balance);
        setShowBalance(true);

        // Notify parent component about the balance retrieval
        if (onBalanceRetrieved) {
          onBalanceRetrieved(currentAccountId, balance);
        }
      }
    });
  };

  const formatCurrency = (
    amount,
    currencyCode = "NGN",
    displaySymbol = null
  ) => {
    try {
      // Use the proper currency code for formatting
      const formattedAmount = new Intl.NumberFormat("en-NG", {
        style: "currency",
        currency: currencyCode,
        minimumFractionDigits: 2,
      }).format(amount);

      // If we have a custom display symbol and it's different from the formatted one, replace it
      if (displaySymbol && displaySymbol !== currencyCode) {
        // Replace the currency symbol that Intl.NumberFormat generated with our custom symbol
        return formattedAmount.replace(/^[^\d\s-]+/g, displaySymbol);
      }

      return formattedAmount;
    } catch (error) {
      console.error("Currency formatting error:", error);
      // Fallback formatting
      return `${displaySymbol || currencyCode} ${amount.toLocaleString(
        "en-NG",
        { minimumFractionDigits: 2 }
      )}`;
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setOtpSent(false);
    setOtpValue("");
    setCurrentAccountId(null);
    setCurrentAccountData(null);
    setShowBalance(false);
    setBalanceData(null);
    setClearInputs(false);
    form.resetFields();
  };

  const resendOTP = () => {
    clearOTPInputs();
    setOtpSent(false);
    setShowBalance(false);
    setBalanceData(null);
    sendOTP(currentAccountId);
  };

  return (
    <Modal
      title="Verify OTP to View Balance"
      open={isModalOpen}
      onCancel={handleCancel}
      centered
      width={500}
      footer={null}
      maskClosable={false}
    >
      <Spin
        spinning={isSendingOTP || isLoading}
        tip={isSendingOTP ? "Sending OTP..." : "Verifying..."}
      >
        <div style={{ padding: "20px 0" }}>
          {!otpSent ? (
            <div style={{ textAlign: "center" }}>
              <p>Sending OTP to your registered email address...</p>
            </div>
          ) : (
            <Form form={form} layout="vertical">
              <div style={{ textAlign: "center", marginBottom: "20px" }}>
                <p>
                  Enter the 5-character OTP sent to your registered email
                  address
                  <strong style={{ color: "#BA24D5" }}>
                    {" "}
                    to view account balance.
                  </strong>
                </p>
              </div>

              <Form.Item name="token" className="centered-error">
                <OTPInput
                  value={otpValue}
                  onChange={setOtpValue}
                  length={5}
                  clearInputs={clearInputs}
                />
              </Form.Item>

              {/* Balance Display Section */}
              {showBalance && balanceData && (
                <div
                  style={{
                    textAlign: "center",
                    marginTop: "20px",
                    padding: "15px",
                    backgroundColor: "#ECFDF3",
                    border: "1px solid #b7eb8f",
                    borderRadius: "8px",
                  }}
                >
                  <Text strong style={{ fontSize: "16px", color: "#389e0d" }}>
                    Account Balance Information
                  </Text>
                  <div style={{ marginTop: "10px" }}>
                    <Text style={{ display: "block", fontSize: "14px" }}>
                      Available Balance:
                    </Text>
                    <Text strong style={{ fontSize: "18px", color: "#BA24D5" }}>
                      {formatCurrency(
                        balanceData.availableBalance,
                        balanceData.currency,
                        balanceData.displaySymbol
                      )}
                    </Text>
                  </div>
                  {balanceData.accountBalance !==
                    balanceData.availableBalance && (
                    <div style={{ marginTop: "8px" }}>
                      <Text style={{ display: "block", fontSize: "14px" }}>
                        Account Balance:
                      </Text>
                      <Text strong style={{ fontSize: "16px" }}>
                        {formatCurrency(
                          balanceData.accountBalance,
                          balanceData.currency,
                          balanceData.displaySymbol
                        )}
                      </Text>
                    </div>
                  )}
                </div>
              )}

              <div style={{ textAlign: "center", marginTop: "30px" }}>
                {!showBalance ? (
                  <>
                    <Button
                      type="primary"
                      onClick={verifyOTPAndGetBalance}
                      disabled={!otpValue || otpValue.length !== 5}
                      style={{
                        backgroundColor: "#BA24D5",
                        color: "white",
                        borderRadius: "8px",
                        marginRight: "10px",
                        minWidth: "100px",
                      }}
                    >
                      Verify
                    </Button>

                    <Button
                      onClick={handleCancel}
                      style={{ minWidth: "100px" }}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={handleCancel}
                    style={{
                      minWidth: "100px",
                      borderRadius: "8px",
                    }}
                  >
                    Close
                  </Button>
                )}
              </div>

              {!showBalance && (
                <div style={{ textAlign: "center", marginTop: "15px" }}>
                  <Button
                    type="link"
                    onClick={resendOTP}
                    disabled={isSendingOTP}
                  >
                    Resend OTP
                  </Button>
                </div>
              )}
            </Form>
          )}
        </div>
      </Spin>
    </Modal>
  );
});

export default OTPBalanceModal;
